import { AfterViewInit, DestroyRef, Directive, effect, ElementRef, inject, input } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { clamp } from '@mos/shared/utils'
import { BehaviorSubject, buffer, debounceTime, map, Observable } from 'rxjs'

export type WheelScrollConfig = {
	/** Scroll in reverse direction (when using flex-direction: column-reverse) */
	reversed?: boolean
	/** Control how much of the content is scrolled per wheel event */
	delta?: number
	/** Warning: 'smooth' doesn't work well with small deltas */
	behavior?: ScrollBehavior
	/** Throttle/buffer wheel events (in ms) */
	throttle?: number
}

/**
 * Allow to control how much of the content is scrolled per wheel event and to throttle/buffer wheel events.
 */
@Directive({
	selector: '[mosWheelScroll]',
	host: {
		'(wheel)': 'onWheel($event)',
		'(scrollend)': 'onScrollEnd()',
	},
})
export class WheelScrollDirective implements AfterViewInit {
	private element = inject(ElementRef<Element>)
	private reversed = false
	private delta = 24
	private target = 0
	private behavior: ScrollBehavior = 'auto'
	private throttle = 2
	private scroll = new BehaviorSubject<number>(0)
	private scroll$?: Observable<number>

	readonly destroyRef = inject(DestroyRef)
	readonly mosWheelScroll = input.required<WheelScrollConfig>()

	constructor() {
		effect(() => {
			const value = this.mosWheelScroll()
			this.reversed = value.reversed ?? this.reversed
			this.delta = value.delta ?? this.delta
			this.behavior = value.behavior ?? this.behavior
			this.throttle = value.throttle ?? this.throttle

			this.scroll$ = this.scroll.pipe(
				buffer(this.scroll.pipe(debounceTime(this.throttle))),
				map((deltas) =>
					deltas.reduce((previous, current) => previous + clamp(current, -this.delta, this.delta), 0),
				),
			)

			this.scroll$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((scroll) => {
				const scrollAmplitude =
					this.element.nativeElement.scrollHeight - this.element.nativeElement.clientHeight
				this.target = clamp(this.target + scroll, this.reversed ? -scrollAmplitude : scrollAmplitude, 0)
				this.element.nativeElement.scrollTo({
					top: this.target,
					behavior: 'auto',
				})
			})
		})
	}

	ngAfterViewInit() {
		// Force scroll to zero position (top or bottom if reversed) on first display
		this.element.nativeElement.scrollTo({
			top: 0,
			behavior: 'smooth',
		})
	}

	onWheel(event: WheelEvent) {
		event.preventDefault()
		this.scroll.next(event.deltaY)
	}

	// Using scrollyfills as a polyfill for Safari
	onScrollEnd() {
		// If another source scrolled the element, update the target when the scroll ends (so we can scroll from there)
		this.target = this.element.nativeElement.scrollTop
	}

	// TODO discuss how to import 'scrollyfills' polyfill (in polyfills.ts and/or in tsconfig.json, in app, in lib)
}
